import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Target, Wallet, TrendingUp } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { getGoals, createGoal, getGoalSuggestions, getDashboardSummary } from "@/lib/api";

export default function Obiettivo() {
  const [goals, setGoals] = useState([]);
  const [sugg, setSugg] = useState(null);
  useEffect(() => { getGoalSuggestions().then((r) => setSugg(r.items || [])).catch(() => setSugg([])); }, []);
  const [nlInput, setNlInput] = useState("");
  const [loading, setLoading] = useState(false);

  const reload = () => Promise.all([
    getGoals(),
    getDashboardSummary().catch(() => null),
  ]).then(([savedGoals, summary]) => {
    const priority = summary?.main_goal;
    setGoals(priority?.source === "consultation" ? [priority] : savedGoals);
  }).catch(() => setGoals([]));
  useEffect(() => { reload(); }, []);
  const consultationGoal = goals.some((g) => g.source === "consultation");

  const submit = async () => {
    if (!nlInput.trim()) return;
    setLoading(true);
    try {
      await createGoal({ natural_language: nlInput });
      toast.success("Obiettivo aggiunto");
      setNlInput("");
      reload();
    } catch {
      toast.error("Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Obiettivo di business"
        title="Il tuo obiettivo"
        subtitle={consultationGoal ? "La priorità emersa dalla consulenza, da tradurre in un obiettivo misurabile." : "Nadia lavora per raggiungere questo risultato entro la fine del periodo."}
      />

      {goals.map((g) => {
        const isConsultation = g.source === "consultation";
        const hasTarget = !isConsultation && g.target != null && g.target > 0;
        const hasBudget = !isConsultation && g.max_budget != null;
        const pct = hasTarget ? Math.min(100, Math.round((g.achieved / g.target) * 100)) : 0;
        const budgetPct = hasBudget && g.max_budget > 0 ? Math.min(100, Math.round((g.current_spend / g.max_budget) * 100)) : 0;
        return (
          <div key={g.id} className="nadia-card p-6 mb-4" data-testid={`goal-card-${g.id}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[11px] text-slate-400 uppercase tracking-wider font-semibold mb-1" data-testid={`goal-label-${g.id}`}>
                  {isConsultation ? "Priorità della consulenza" : g.period ? `Obiettivo del ${g.period}` : "Obiettivo"}
                </div>
                <div className="font-heading text-3xl font-bold text-slate-50" data-testid={`goal-title-${g.id}`}>{g.title}</div>
              </div>
              {hasTarget && <span className="hidden sm:inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-cyan-500/10 text-cyan-300 border border-cyan-500/25" data-testid={`goal-completion-${g.id}`}>
                {pct}% completato
              </span>}
            </div>

            {isConsultation && (
              <p className="text-sm text-slate-400" data-testid="consultation-priority-details">
                Priorità ripresa dalla consulenza. Traguardo numerico, periodo e budget da definire.
              </p>
            )}
            {(hasTarget || hasBudget) && <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
              {hasTarget && <Cell testId={`goal-achieved-${g.id}`} icon={Target} label="Ottenute" value={g.achieved} sub={`di ${g.target}`} />}
              {hasBudget && <Cell testId={`goal-spend-${g.id}`} icon={Wallet} label="Spesa" value={`€${g.current_spend}`} sub={`di €${g.max_budget}`} />}
              {g.cost_per_result != null && <Cell testId={`goal-cost-${g.id}`} icon={TrendingUp} label="Costo/risultato" value={`€${g.cost_per_result.toFixed(2)}`} />}
              {hasTarget && <Cell testId={`goal-remaining-${g.id}`} icon={Target} label="Residuo" value={Math.max(0, g.target - (g.achieved ?? 0))} />}
            </div>}

            {hasTarget && <div data-testid={`goal-progress-${g.id}`}>
              <div className="flex justify-between text-xs text-slate-400 mb-1">
                <span>Progresso obiettivo</span>
                <span className="font-mono">{g.achieved}/{g.target}</span>
              </div>
              <Progress value={pct} className="h-2 bg-white/5" />
            </div>}
            {hasBudget && <div data-testid={`goal-budget-${g.id}`}>
              <div className="flex justify-between text-xs text-slate-400 mt-4 mb-1">
                <span>Budget utilizzato</span>
                <span className="font-mono">€{g.current_spend}/€{g.max_budget}</span>
              </div>
              <Progress value={budgetPct} className="h-2 bg-white/5" />
            </div>}
          </div>
        );
      })}

      {goals.length === 0 && (
        <div className="nadia-card p-6 mb-4" data-testid="goal-suggestions">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Obiettivi che Nadia ti propone in base alla tua attività</div>
          {sugg === null ? <div className="text-sm text-slate-500">Nadia sta preparando le proposte…</div> : sugg.length === 0 ? <div className="text-sm text-slate-500">Analizza prima la tua attività dal Business Brain per ricevere proposte.</div> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {sugg.map((g, i) => (
                <button key={i} onClick={() => setNlInput(`${g.title} (KPI: ${g.kpi}, ${g.period_days || 30} giorni)`)} className="text-left rounded-xl border border-white/10 bg-white/[0.02] p-3 hover:border-cyan-400/40 transition-colors" data-testid={`goal-suggestion-${i}`}>
                  <div className="text-sm text-slate-100 font-semibold">{g.title}</div>
                  <div className="text-[11px] text-slate-400">KPI: {g.kpi} · {g.period_days || 30} giorni{g.why ? ` · ${g.why}` : ""}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add new goal */}
      <div className="nadia-card p-6" data-testid="new-goal-form">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
          Aggiungi un nuovo obiettivo
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            value={nlInput}
            onChange={(e) => setNlInput(e.target.value)}
            placeholder="Es. Voglio 50 registrazioni con massimo €400"
            className="flex-1 bg-[#0B1120] border border-white/10 rounded-xl px-4 py-3 text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/60 text-sm"
            data-testid="new-goal-input"
          />
          <Button
            onClick={submit}
            disabled={loading}
            className="bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 hover:from-cyan-300 hover:to-violet-400 font-semibold"
            data-testid="submit-new-goal"
          >
            <Plus size={16} className="mr-2" /> Aggiungi
          </Button>
        </div>
      </div>
    </>
  );
}

function Cell({ testId, icon: Icon, label, value, sub }) {
  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[11px] text-slate-500 uppercase tracking-wider font-semibold">
        <Icon size={12} strokeWidth={1.75} /> {label}
      </div>
      <div className="font-heading text-xl font-bold text-slate-100">{value}</div>
      {sub ? <div className="text-xs text-slate-500">{sub}</div> : null}
    </div>
  );
}
